/**
 * Integrations & workflows catalog — the shared model behind the "Integrations" hub (a top-level
 * surface listing every automation that flows through Lapo: the Anvil connection, the Daily Agenda,
 * and the growing roster of workloads still being stubbed in — contact pictures, the email digest,
 * and more).
 *
 * This module is the single source of truth for the STATIC metadata (name, icon, description, which
 * linked account makes a workload live, whether it can be run on demand). The server derives each
 * integration's LIVE status + audit log from this catalog plus the user's real connected accounts;
 * the client renders it and falls back to {@link INTEGRATIONS} — a demo catalog built from the same
 * definitions — when it's offline or the API isn't configured.
 *
 * Timestamps on the client-facing DTO are `agoMinutes` offsets (minutes before "now"), resolved by
 * {@link relativeTime}. The server stores audit records with absolute epoch-ms `at` and converts them
 * to offsets at request time (see {@link auditRecordToEntry}); keeping the wire format relative means
 * the seed/demo catalog never goes stale on the shelf.
 */

/**
 * Lifecycle state of an integration, in rough order of "needs attention":
 *  - `active`      — connected and running.
 *  - `paused`      — available but not connected/enabled by the user.
 *  - `error`       — connected but its last run failed / it needs re-auth.
 *  - `coming-soon` — stubbed placeholder; not yet available to enable.
 */
export type IntegrationStatus = 'active' | 'paused' | 'error' | 'coming-soon';

/** Broad grouping for the filter menu — lets the hub scope cards to a family of workloads. */
export type IntegrationCategory = 'connection' | 'productivity' | 'communication' | 'enrichment';

/**
 * How an integration is connected/managed from its detail page. Drives which interactive panel the
 * hub renders (a connect/disconnect control, a credential form, or the authorized-apps manager) in
 * addition to the read-only config + audit log. Absent for pure workloads (e.g. the Daily Agenda),
 * which are configured elsewhere and only run.
 *  - `todoist`         — OAuth connect/disconnect (webhook ingest).
 *  - `easypost`        — pasted API key + ship-from (return) address.
 *  - `amazon`          — legacy Login-with-Amazon panel (CREEL D21: removed in the legacy-removal phase;
 *                        Amazon is now configured in-place via the Settings section, voice-calling style).
 *  - `granola`         — pasted Granola API key + a poll toggle (meeting notes → the journal).
 *  - `authorized-apps` — the Lapo-as-OAuth-provider manager (Anvil and other third-party grants).
 */
export type IntegrationConnectKind = 'todoist' | 'easypost' | 'amazon' | 'granola' | 'authorized-apps' | 'financial' | 'slack' | 'tesseta';

/** The most-recent-action summary shown on a card and atop the detail page. */
export interface IntegrationActivity {
  /** How long ago the action happened, in minutes before "now". Resolved by {@link relativeTime}. */
  agoMinutes: number;
  /** One-line human summary, e.g. "Synced 12 events". */
  summary: string;
  /** Whether the action succeeded — drives the dot color beside it. */
  ok: boolean;
}

/** A single read-only configuration field surfaced on the detail page (advanced config). */
export interface IntegrationConfigField {
  label: string;
  value: string;
  /** Optional helper text under the value. */
  hint?: string;
}

/** Severity of an audit-log line. */
export type IntegrationAuditLevel = 'info' | 'warn' | 'error';

/** One entry in an integration's audit log, client-facing (relative time). Newest-first. */
export interface IntegrationAuditEntry {
  agoMinutes: number;
  event: string;
  detail?: string;
  level: IntegrationAuditLevel;
}

/** A persisted audit record (server-side truth): same as an entry but with an absolute epoch-ms `at`. */
export interface IntegrationAuditRecord {
  at: number;
  event: string;
  detail?: string;
  level: IntegrationAuditLevel;
}

/**
 * A single integration/workflow — the client-facing DTO the hub renders. Combines the static
 * definition with the user's live status, most-recent action, resolved config, and audit log.
 */
export interface Integration {
  /** Stable slug id, used in the URL hash and as the React key. */
  id: string;
  name: string;
  description: string;
  /** Google Material Symbols icon name (snake_case), e.g. "hub". */
  icon: string;
  category: IntegrationCategory;
  status: IntegrationStatus;
  /**
   * Master on/off switch for this integration (per-user). When `false` the integration is SUSPENDED:
   * its configuration is preserved, but it never runs (background jobs + webhooks skip it) and its
   * owned UI surfaces (side-menu items, run controls) hide. Absent state defaults to `true` (enabled).
   */
  enabled: boolean;
  /** Whether the card/detail shows a Run button (a workload that can be triggered on demand). */
  runnable: boolean;
  /** When set, the detail page renders the matching connect/manage panel (see {@link IntegrationConnectKind}). */
  connect?: IntegrationConnectKind;
  lastAction?: IntegrationActivity;
  config: IntegrationConfigField[];
  audit: IntegrationAuditEntry[];
}

/**
 * Static definition of one integration — the source of truth the server reads to compute live state.
 * `requires` lists the linked-account integration keys (see the server's `LinkedAccount.integrations`,
 * e.g. `calendar`, `gmail`, `todoist`) whose presence marks this workload connected; an empty list
 * means it needs no account (e.g. the Anvil runtime connection). `available` is whether the workload
 * is actually built yet — a not-yet-built one always reads as `coming-soon`, regardless of accounts.
 */
export interface IntegrationDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: IntegrationCategory;
  runnable: boolean;
  available: boolean;
  requires: string[];
  /** When set, the detail page renders the matching connect/manage panel (see {@link IntegrationConnectKind}). */
  connect?: IntegrationConnectKind;
  config: IntegrationConfigField[];
  /**
   * NEXUS: one-line, model-facing summary of what data/actions this integration exposes to the AI
   * assistant, e.g. "Read-only: account balances and transactions." Absent for integrations that
   * contribute no capability to the agent. This is app-authored, enumerated text — never user content —
   * so it is safe to place in the assistant's system prompt (RELAY D11).
   */
  aiSummary?: string;
  /**
   * NEXUS: when true, this capability's read tools are only offered to the agent after an explicit
   * per-user opt-in (financial-grade PII). Enablement alone is not enough. Default false.
   */
  sensitive?: boolean;
  /** Seed used only to build the offline/demo {@link INTEGRATIONS} catalog. */
  demo: { status: IntegrationStatus; lastAction?: IntegrationActivity; audit: IntegrationAuditEntry[] };
}

/** Human label for a status (badge text). */
export function statusLabel(status: IntegrationStatus): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'paused':
      return 'Not connected';
    case 'error':
      return 'Needs attention';
    case 'coming-soon':
      return 'Coming soon';
  }
}

/**
 * Whether an integration is enabled given the per-user enablement map. Absent entry → enabled (true),
 * so a fresh install and every not-yet-touched integration runs by default.
 */
export function isIntegrationEnabled(map: Record<string, boolean> | null | undefined, id: string): boolean {
  return map?.[id] !== false;
}

/**
 * Render an `agoMinutes` offset as a compact relative label ("just now", "5m ago", "2h ago",
 * "3d ago"). Kept pure (no `Date.now()`) so callers pass their own clock and tests stay deterministic.
 */
export function relativeTime(agoMinutes: number): string {
  if (agoMinutes < 1) return 'just now';
  if (agoMinutes < 60) return `${Math.round(agoMinutes)}m ago`;
  const hours = agoMinutes / 60;
  if (hours < 24) return `${Math.round(hours)}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

/** Minutes between an absolute epoch-ms timestamp and `now` (never negative). */
export function agoMinutes(at: number, now: number): number {
  return Math.max(0, Math.round((now - at) / 60_000));
}

/** Convert a stored audit record to the client-facing (relative-time) entry. */
export function auditRecordToEntry(rec: IntegrationAuditRecord, now: number): IntegrationAuditEntry {
  return { agoMinutes: agoMinutes(rec.at, now), event: rec.event, detail: rec.detail, level: rec.level };
}

/** Derive the "most recent action" line from a stored audit record. */
export function activityFromRecord(rec: IntegrationAuditRecord, now: number): IntegrationActivity {
  const summary = rec.detail ? `${rec.event} — ${rec.detail}` : rec.event;
  return { agoMinutes: agoMinutes(rec.at, now), summary, ok: rec.level !== 'error' };
}

/**
 * Compute an integration's live status from its definition and whether the user has connected any of
 * its required accounts. A not-yet-built workload is always `coming-soon`; an account-less one that is
 * built is always `active` (e.g. the Anvil connection); otherwise it tracks whether an account is
 * linked.
 */
export function computeStatus(def: IntegrationDefinition, connected: boolean): IntegrationStatus {
  if (!def.available) return 'coming-soon';
  if (def.requires.length === 0) return 'active';
  return connected ? 'active' : 'paused';
}

/** Whether a definition can be triggered on demand right now (built AND declared runnable). */
export function isRunnable(def: IntegrationDefinition): boolean {
  return def.available && def.runnable;
}

/** Filter inputs for the hub's filter menu: free-text query + an optional status scope. */
export interface IntegrationFilter {
  /** Case-insensitive match against name + description. Empty matches everything. */
  query?: string;
  /** When set (not `'all'`), keep only integrations in this status. */
  status?: IntegrationStatus | 'all';
}

/**
 * Apply the hub's filter to a list of integrations. Pure and order-preserving — the catalog's own
 * ordering (roughly: live workloads first, stubs last) is what the grid shows.
 */
export function filterIntegrations(list: Integration[], filter: IntegrationFilter = {}): Integration[] {
  const q = (filter.query ?? '').trim().toLowerCase();
  const status = filter.status ?? 'all';
  return list.filter((i) => {
    if (status !== 'all' && i.status !== status) return false;
    if (!q) return true;
    return i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q);
  });
}

/** Look up one integration by id. */
export function findIntegration(list: Integration[], id: string): Integration | undefined {
  return list.find((i) => i.id === id);
}

/** Look up a static definition by id. */
export function findDefinition(id: string): IntegrationDefinition | undefined {
  return INTEGRATION_DEFS.find((d) => d.id === id);
}

/**
 * The built-in catalog of definitions. The Daily Agenda is the flagship workload (it tracks whether a
 * Google Calendar is linked); Todoist, EasyPost, and Amazon Business are user-connected services
 * managed right from their detail page; Authorized Apps is the Lapo-as-OAuth-provider manager (where
 * Anvil and other third-party grants live). Add to this list as new integrations land; the hub and
 * the API pick them up with no other changes.
 */
export const INTEGRATION_DEFS: IntegrationDefinition[] = [
  {
    id: 'daily-agenda',
    aiSummary: 'Read your calendar/agenda for a day range (meetings, times).',
    name: 'Daily Agenda',
    description: "Builds each morning's agenda from your connected calendars and posts it to today's journal.",
    icon: 'event',
    category: 'productivity',
    runnable: true,
    available: true,
    requires: ['calendar'],
    config: [
      { label: 'Schedule', value: 'Daily at 6:00 AM', hint: 'When the agenda is composed and posted.' },
      { label: 'Calendars', value: 'All connected', hint: 'Every calendar on your linked Google accounts.' },
      { label: 'Destination', value: "Today's journal" },
    ],
    demo: {
      status: 'active',
      lastAction: { agoMinutes: 320, summary: 'Posted 6 events to today', ok: true },
      audit: [
        { agoMinutes: 320, event: 'Agenda posted', detail: '6 events for today', level: 'info' },
        { agoMinutes: 1760, event: 'Agenda posted', detail: '4 events for yesterday', level: 'info' },
      ],
    },
  },
  {
    id: 'weather',
    name: 'Weather',
    description: "Adds a one-line local forecast to the top of your morning brief. No API key — a free, keyless weather source.",
    icon: 'partly_cloudy_day',
    category: 'enrichment',
    // Not an on-demand workload: the line is composed with the morning brief, not run standalone.
    runnable: false,
    available: true,
    // No linked account — Open-Meteo is keyless. Configured in-place via the Settings panel (location).
    requires: [],
    config: [
      { label: 'Source', value: 'Open-Meteo (no API key)', hint: 'A free, keyless weather + geocoding API.' },
      { label: 'Location', value: 'Your configured place, or your timezone', hint: 'Blank location falls back to your timezone’s city.' },
      { label: 'Units', value: 'Fahrenheit or Celsius', hint: 'Set in the Weather settings panel.' },
      { label: 'Destination', value: 'The top of your morning brief' },
    ],
    demo: {
      status: 'active',
      lastAction: { agoMinutes: 320, summary: 'Added forecast: 61° / 49°, partly cloudy', ok: true },
      audit: [{ agoMinutes: 320, event: 'Forecast added', detail: 'Morning brief weather line', level: 'info' }],
    },
  },
  {
    id: 'todoist',
    aiSummary: 'Read and add to-do tasks (open/overdue/due-today counts, add reminders).',
    name: 'Todoist',
    description: 'Ingests tasks from Todoist in real time via webhook and routes them through the engine pipeline.',
    icon: 'checklist',
    category: 'productivity',
    runnable: false,
    available: true,
    requires: ['todoist'],
    connect: 'todoist',
    config: [
      { label: 'Webhook', value: 'Real-time (HMAC-verified)' },
      { label: 'Account', value: 'Linked Todoist account' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Not connected', detail: 'Link a Todoist account to enable.', level: 'info' }],
    },
  },
  {
    id: 'easypost',
    aiSummary: 'Draft shipping labels for review and track packages by number; list tracked shipments.',
    name: 'EasyPost',
    description: 'Connect EasyPost to buy shipping labels and track packages, with your return address on every label.',
    icon: 'local_shipping',
    category: 'productivity',
    runnable: false,
    available: true,
    requires: ['easypost'],
    connect: 'easypost',
    config: [
      { label: 'Credential', value: 'API key (stored encrypted)' },
      { label: 'Return address', value: 'Configured below' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Not connected', detail: 'Paste an EasyPost API key to enable.', level: 'info' }],
    },
  },
  {
    // CREEL (D21): the legacy Login-with-Amazon integration is replaced by Clavero. Id `amazon` is
    // kept (stable menu/capability key) but the whole surface is now the Clavero-backed shopping flow.
    id: 'amazon',
    aiSummary: 'Read your Amazon cart and orders, search products, and draft priced Amazon orders for confirmation.',
    name: 'Amazon',
    description:
      'Shop Amazon from Lapo through your Clavero service: read your cart and order history, search products, and place priced orders through a confirm flow — with Clavero spend caps and a kill-switch behind everything.',
    icon: 'shopping_bag',
    category: 'productivity',
    runnable: false,
    available: true,
    // No linked provider account — Clavero is a per-user BYO endpoint (D3), configured in-place via the
    // Settings section (voice-calling pattern), so no connect-panel `connect` kind is set.
    requires: [],
    // Financial-grade action surface (spends money): the agent's read tools require explicit opt-in.
    sensitive: true,
    config: [
      { label: 'Endpoint', value: 'Clavero base URL (Tailscale Funnel)', hint: 'Your Clavero service, e.g. https://mac-mini-m1.softshell-mark.ts.net:10000' },
      { label: 'Credentials', value: 'Intent + confirm bearer tokens (stored encrypted)', hint: 'Two scope-split tokens; the confirm token authorizes spending and is used only server-side.' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Not connected', detail: 'Add your Clavero endpoint and tokens to enable Amazon shopping.', level: 'info' }],
    },
  },
  {
    id: 'contact-pictures',
    name: 'Contact Pictures',
    description:
      'A studio that generates a polished synthetic headshot for a contact from photos you upload, then writes it back to Google Contacts.',
    icon: 'account_circle',
    category: 'enrichment',
    // Not a one-click "Run" workload — it's a per-contact wizard (upload → style → generate → approve),
    // so the detail page renders the studio instead of a Run button.
    runnable: false,
    available: true,
    requires: ['people'],
    config: [
      { label: 'Source', value: 'Gemini (Nano Banana)', hint: 'Synthetic headshots generated from reference photos you upload.' },
      { label: 'Styles', value: 'Modern, Business, Formal, Casual, Creative' },
      { label: 'Write-back', value: 'Google Contacts', hint: 'Approved headshots update the Google contact and propagate across Google.' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Ready', detail: 'Connect Google Contacts, then pick a contact to generate a headshot.', level: 'info' }],
    },
  },
  {
    id: 'subscription-digest',
    aiSummary: 'Read the latest newsletter/subscription digest issue.',
    name: 'Subscription Digest',
    description:
      'Clusters your newsletter and subscription email into a topic-grouped digest, emailed to you and readable in the app.',
    icon: 'newspaper',
    category: 'communication',
    runnable: true,
    available: true,
    requires: ['gmail'],
    config: [
      { label: 'Source', value: 'Gmail labels', hint: 'Subscription-labeled mail across every linked mailbox.' },
      { label: 'Clustering', value: 'Two-stage Claude', hint: 'Stories grouped across newsletters by topic.' },
      { label: 'Delivery', value: 'Email + in-app reader', hint: 'Sent via email and posted to the Subscription view.' },
      { label: 'Schedule', value: 'Twice daily (morning & afternoon)' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Ready', detail: 'Connect Gmail, then enable to build a digest.', level: 'info' }],
    },
  },
  {
    id: 'granola',
    name: 'Granola',
    description:
      "After each meeting ends, polls Granola for its AI notes and drops the markdown into that meeting's entry in your journal.",
    icon: 'summarize',
    category: 'productivity',
    runnable: true,
    available: true,
    requires: ['granola'],
    connect: 'granola',
    config: [
      { label: 'Credential', value: 'API key (stored encrypted)', hint: 'A Granola Business/Enterprise API key (grn_…).' },
      { label: 'Source', value: 'Granola public API', hint: 'Notes are matched to a meeting by its calendar event.' },
      { label: 'Destination', value: "The meeting's entry in that day's journal" },
      { label: 'Polling', value: 'Every ~15 min with backoff', hint: 'Retries until notes are ready or the meeting expires.' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Not connected', detail: 'Paste a Granola API key to enable.', level: 'info' }],
    },
  },
  {
    id: 'gemini',
    name: 'Gemini Notes',
    description:
      "After each Google Meet call, imports Gemini's \"Take notes for me\" summary and nests it under that meeting's entry in your journal.",
    icon: 'auto_awesome',
    category: 'productivity',
    runnable: true,
    available: true,
    // Rides the linked Google account (no separate credential) — the extra Meet/Drive read scopes are
    // granted by re-linking Google. `requires: ['calendar']` marks it live once a Google account is linked.
    requires: ['calendar'],
    config: [
      { label: 'Credential', value: 'Your linked Google account', hint: 'Reconnect Google to grant Meet + Drive read access.' },
      { label: 'Source', value: 'Google Workspace', hint: 'Drive "Meet Recordings", the notes email, the Meet API, and the calendar attachment.' },
      { label: 'Destination', value: "The meeting's entry in that day's journal" },
      { label: 'Polling', value: 'Every ~15 min with backoff', hint: 'Retries until notes are ready or the meeting expires.' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Not connected', detail: 'Link Google, then enable to import meeting notes.', level: 'info' }],
    },
  },
  {
    id: 'movie-enricher',
    name: 'Movie Enricher',
    description:
      "Each night, adds every movie & show in your Todoist project its IMDb rating and where it's streaming in the US.",
    icon: 'movie',
    category: 'enrichment',
    runnable: true,
    available: true,
    requires: ['todoist'],
    config: [
      { label: 'Source', value: 'Todoist project', hint: 'Every task in the project you name (default "Movies").' },
      { label: 'Ratings', value: 'IMDb via OMDb' },
      { label: 'Streaming', value: 'US availability via TMDB (JustWatch)' },
      { label: 'Schedule', value: 'Daily at 2:00 AM', hint: 'Only the auto-managed line is rewritten; your own notes are kept.' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Not connected', detail: 'Link Todoist, then enable to enrich your movie list.', level: 'info' }],
    },
  },
  {
    id: 'note-resurfacing',
    aiSummary: 'Weekly digest of forgotten notes and decisions relevant to current work.',
    name: 'Note Resurfacing',
    description:
      'Each Monday, finds dormant notes and decisions connected to what you’re working on now and surfaces the few worth another look.',
    icon: 'history',
    category: 'enrichment',
    runnable: true,
    available: true,
    requires: [],
    config: [
      { label: 'Source', value: 'Your own notes & decisions', hint: 'Documents and decisions untouched for 60+ days — nothing new is ingested.' },
      { label: 'Selection', value: 'Semantic match + model pick', hint: 'Matched against active initiatives and recent pages; at most 5 items, quiet weeks stay quiet.' },
      { label: 'Destination', value: 'Daily Signals', hint: 'One Monday card; per-item “Not relevant” teaches it what to skip.' },
      { label: 'Schedule', value: 'Weekly + Run now', hint: 'Mondays 6:00 AM; re-runs in the same week update the same card.' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Off', detail: 'Enable to get the Monday “worth another look” digest.', level: 'info' }],
    },
  },
  {
    id: 'decision-ledger',
    aiSummary: 'Read logged decisions (what was decided, by whom, traced to the source).',
    name: 'Decision Ledger',
    description:
      'Each night, mines the day’s meetings, Slack, and email for the decisions they made — logged, traced back to the source, and searchable.',
    icon: 'gavel',
    category: 'enrichment',
    runnable: true,
    available: true,
    requires: [],
    config: [
      { label: 'Source', value: 'Already-ingested comms', hint: 'Journal & meeting notes, Slack digests, synced email — no new access.' },
      { label: 'Extraction', value: 'Two-stage Claude', hint: 'Haiku extracts per source; Sonnet clusters, dedupes, and validates.' },
      { label: 'Destination', value: 'Decision Ledger view', hint: 'A dated, faceted, searchable table with per-decision source deep-links.' },
      { label: 'Schedule', value: 'Nightly + Run now', hint: 'Also a one-time 30-day backfill on first enable.' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Not connected', detail: 'Link a Claude subscription (Models), then enable to log your decisions.', level: 'info' }],
    },
  },
  {
    id: 'restaurant-verifier',
    name: 'Restaurant Verifier',
    description:
      'Each night, refreshes every restaurant in your Todoist project with its Google rating, reviews, address, and closure status.',
    icon: 'restaurant',
    category: 'enrichment',
    runnable: true,
    available: true,
    requires: ['todoist'],
    config: [
      { label: 'Source', value: 'Todoist project', hint: 'Every task in the project you name (default "Restaurants (Atlanta)").' },
      { label: 'Data', value: 'Google Places (New)', hint: 'Rating, review count, address, cuisine, and closure status.' },
      { label: 'Closures', value: 'Flagged, never deleted', hint: 'Permanently-closed places are flagged in the description only.' },
      { label: 'Schedule', value: 'Daily at 2:00 AM' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Not connected', detail: 'Link Todoist, then enable to verify your restaurant list.', level: 'info' }],
    },
  },
  {
    id: 'instagram-share',
    name: 'Instagram Share',
    description:
      "Share Instagram posts, reels & stories from your phone into today's journal — parsed, re-hosted, and tagged.",
    icon: 'photo_camera',
    category: 'enrichment',
    // Not an on-demand workload — imports fire when you share from your phone, so there's nothing to "run".
    runnable: false,
    available: true,
    // Needs no linked account: the share arrives over the email ingest pipeline; only the pasted
    // Scrape Creators key (configured below) gates the import.
    requires: [],
    config: [
      { label: 'Credential', value: 'Scrape Creators API key (stored encrypted)', hint: 'Get a key at scrapecreators.com — the free tier covers personal use.' },
      { label: 'Source', value: 'Phone share sheet', hint: 'Posts/reels are scraped by URL; story images are read with Gemini OCR.' },
      { label: 'Media', value: 'Re-hosted to your workspace', hint: 'Images are copied off Instagram so journal entries never break.' },
      { label: 'Destination', value: "Today's journal", hint: 'Tagged #instagram plus the handle and content tags.' },
    ],
    demo: {
      status: 'active',
      audit: [{ agoMinutes: 0, event: 'Ready', detail: 'Paste a Scrape Creators API key, then share a post from your phone.', level: 'info' }],
    },
  },
  {
    id: 'voice-calling',
    aiSummary: 'Place outbound AI voice calls (draft-then-confirm) and read past call outcomes.',
    name: 'Vapi Calling',
    description:
      'Place an outbound phone call handled by an AI voice agent to accomplish a task — book a reservation, confirm an appointment, ask a business a question — then drops the transcript and outcome into your journal.',
    icon: 'call',
    category: 'productivity',
    // Not an on-demand hub "run" — calls are placed by asking Lapo in chat/Telegram (draft-then-confirm),
    // not from a Run button. `available` so the connect panel (per-user Vapi key + assistant/number ids +
    // daily cap + webhook token) is reachable; a live call still needs the owner's DIAL-00 spike.
    runnable: false,
    available: true,
    // BYO-key, no linked account: the pasted Vapi API key (KMS-encrypted, configured below) is all that
    // gates it — mirrors the Instagram Share / Restaurant Verifier BYO-key model.
    requires: [],
    config: [
      { label: 'Credential', value: 'Vapi API key (stored encrypted)', hint: 'A Vapi private API key — get one at vapi.ai. Per-user: your key, your Vapi account.' },
      { label: 'Voice agent', value: 'Your Vapi assistant + number', hint: 'The assistant id that runs the call and the phone-number id it calls from.' },
      { label: 'Webhook', value: 'Your personal webhook token', hint: "Paste this token (shown after you save) as your Vapi assistant's Server URL secret so call results come back to you." },
      { label: 'Guardrail', value: 'Daily call cap', hint: 'The max calls Lapo will place per day (default 10) — a runaway-loop backstop.' },
      { label: 'Privacy', value: 'Transcripts encrypted, auto-deleted', hint: 'Call transcripts are stored encrypted and purged after 30 days; only the summary is kept.' },
      { label: 'Destination', value: "Today's journal + chat", hint: 'The outcome lands as a journal signal, in your assistant chat, and (if linked) Telegram.' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Not connected', detail: 'Paste a Vapi API key to enable.', level: 'info' }],
    },
  },
  {
    id: 'reddit-monitor',
    aiSummary: 'Watch subreddits for posts matching your keywords (e.g. watch sales) and read the matches.',
    name: 'Reddit Monitor',
    description:
      'Watch any set of subreddits for new posts matching your search terms — a watch you want, a part for sale, a topic — and read the filtered matches (with seller reputation and comment counts) in the Reddit panel.',
    icon: 'forum',
    category: 'productivity',
    // BYO-key, no linked account: a redditapis.com key (KMS-encrypted, configured below) gates it —
    // mirrors the Voice / Restaurant Verifier BYO-key model. Results are fetched on demand from the
    // Reddit panel (each call spends redditapis credits), not run from a hub Run button.
    runnable: false,
    available: true,
    requires: [],
    config: [
      { label: 'Credential', value: 'redditapis.com API key (stored encrypted)', hint: 'A key from redditapis.com — a third-party reseller of Reddit access (~$0.002/read). Reddit’s own API no longer self-serves.' },
      { label: 'Channels', value: 'Subreddits + keyword filters', hint: 'Each channel is a subreddit plus include/exclude search terms; a post is kept when it matches.' },
      { label: 'Fetch size', value: 'Posts scanned per subreddit', hint: 'Newest N posts pulled per subreddit per refresh (1–100) — bounds the per-refresh cost.' },
      { label: 'Destination', value: 'The Reddit panel', hint: 'Matches show in the Reddit side-panel; fetched on demand via its Refresh button.' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Not connected', detail: 'Paste a redditapis key to enable.', level: 'info' }],
    },
  },
  {
    id: 'financial-management',
    aiSummary: 'Read-only: account balances, transactions, and recurring charges (sensitive — opt-in).',
    // NEXUS-04: financial data is financial-grade PII — its read tools are withheld from the assistant
    // until the user explicitly opts in (a single toggle on this connect panel), even once connected.
    sensitive: true,
    name: 'Financial Management',
    description:
      'Connect your bank, card, and investment accounts through Plaid. Each night it syncs and normalizes every transaction, detects recurring payments and duplicate subscriptions, and tracks your net worth.',
    icon: 'account_balance',
    category: 'productivity',
    runnable: true,
    available: true,
    requires: ['financial'],
    connect: 'financial',
    config: [
      { label: 'Source', value: 'Plaid', hint: 'Bank/card/investment accounts you connect via Plaid Link.' },
      { label: 'Sync', value: 'Nightly (cursor-based)', hint: 'Transactions, balances, liabilities, and holdings.' },
      { label: 'Insights', value: 'Categorized spend + recurring detection', hint: 'Normalized categories and duplicate-subscription flags.' },
      { label: 'Destination', value: 'The Finances dashboard', hint: 'Financial data stays out of your notes.' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Not connected', detail: 'Connect a bank via Plaid to enable.', level: 'info' }],
    },
  },
  {
    id: 'bill-pay',
    aiSummary: 'Read-only: your bills and their pay status/history — no card data beyond a nickname + last4 (sensitive — opt-in).',
    // REMIT (Bill Pay) — financial-grade. Its one read tool (`list_bills`) is withheld from the
    // assistant until the user explicitly opts in, even once enabled. It NEVER gets an action tool:
    // money moves only through the signal surface (P2). See docs/plans/bill-pay-remit.md.
    sensitive: true,
    name: 'Bill Pay',
    description:
      'Lapo captures bills from your email and photos of paper mail, then — on your approval — navigates the biller’s payment site in a cloud browser and pays the bill with a card you store here, archiving a receipt and reconciling the charge against your bank feed. Your card number never enters the AI’s context.',
    icon: 'receipt_long',
    category: 'productivity',
    runnable: false,
    available: true,
    requires: [],
    config: [
      { label: 'Execution', value: 'Bright Data Browser API (your key)', hint: 'A cloud browser pays guest-pay flows; you bring your own Bright Data zone key.' },
      { label: 'Cards', value: 'Stored encrypted (KMS)', hint: 'Full card is encrypted per-user; only a nickname + last4 are ever shown.' },
      { label: 'Auto-pay', value: 'Prior-paid billers under your cap', hint: 'First-time billers and over-cap bills always ask before paying.' },
      { label: 'Reconcile', value: 'Nightly against Plaid', hint: 'A paid bill with no matching bank charge raises an alert.' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Not configured', detail: 'Add a card and your Bright Data key to enable.', level: 'info' }],
    },
  },
  {
    id: 'tesseta',
    aiSummary:
      'Read-only: weight & body-composition trends (body fat %, lean mass, DEXA) and workout compliance vs your training programs (sensitive — opt-in).',
    // Health/labs data is medical-grade PII — its read tools are withheld from the assistant until the
    // user explicitly opts in (a toggle on this connect panel), even once connected. Mirrors financial.
    sensitive: true,
    name: 'Fitness (Tesseta)',
    description:
      'Connect your Tesseta personal health record. Each night Lapo syncs your weight and body-composition history (body fat %, lean mass, DEXA scans) and your training programs and completed workouts, then charts your trends and workout compliance on the Fitness page.',
    icon: 'fitness_center',
    category: 'productivity',
    runnable: true,
    available: true,
    requires: ['tesseta'],
    connect: 'tesseta',
    config: [
      { label: 'Authorization', value: 'OAuth 2.0 (Authorization Code + PKCE)', hint: 'Read-only, user-delegated access to api.tesseta.com.' },
      { label: 'Sync', value: 'Nightly', hint: 'Body composition, DEXA scans, training programs, and completed workouts.' },
      { label: 'Insights', value: 'Weight/body-comp trends + workout compliance', hint: 'Scheduled vs completed sessions, streaks, per-program compliance %.' },
      { label: 'Destination', value: 'The Fitness dashboard', hint: 'Health data stays out of your notes.' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Not connected', detail: 'Connect Tesseta to enable.', level: 'info' }],
    },
  },
  {
    id: 'slack',
    aiSummary: "Read the latest Slack 'did I miss anything' digest of channels, DMs, and mentions.",
    name: 'Slack',
    description:
      'Connect your Slack workspaces so Lapo reads as you and, each night, posts a "did I miss anything" digest of your channels, DMs, and mentions to that day\'s journal.',
    icon: 'forum',
    category: 'communication',
    runnable: true,
    available: true,
    // Requires at least one linked Slack workspace (user OAuth). Multi-workspace: each is its own account.
    requires: ['slack'],
    connect: 'slack',
    config: [
      { label: 'Authorization', value: 'User OAuth (per workspace)', hint: 'Lapo reads and replies as you — no bot user.' },
      { label: 'Source', value: 'Public/private channels + DMs + group DMs', hint: 'Everything you\'re a member of, filtered by your mutes + allow/deny lists.' },
      { label: 'Summarizer', value: 'Two-stage Claude (your Models token)', hint: 'Ranked and grouped into DMs / mentions / threads / channel highlights.' },
      { label: 'Destination', value: "One 'Slack Digest' block in that day's journal" },
      { label: 'Schedule', value: 'Nightly (configurable) + Run now' },
      { label: 'Replies', value: 'Draft-first — you approve before anything sends', hint: 'Compose a reply from a digest item; nothing is sent without your tap.' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Not connected', detail: 'Link a Slack workspace to enable.', level: 'info' }],
    },
  },
  {
    id: 'authorized-apps',
    name: 'Authorized Apps',
    description: 'Third-party apps you’ve authorized to read and append to your workspace (Anvil and others). Review their permissions or revoke access.',
    icon: 'apps',
    category: 'connection',
    // A management surface, not a workload — you review/revoke grants, there's nothing to "run".
    runnable: false,
    available: true,
    requires: [],
    connect: 'authorized-apps',
    config: [
      { label: 'Provider', value: 'Lapo (OAuth 2.0)', hint: 'Apps authorize against Lapo’s OAuth connection.' },
      { label: 'Scopes', value: 'journal.append, documents.write' },
    ],
    demo: {
      status: 'active',
      audit: [{ agoMinutes: 0, event: 'Ready', detail: 'Authorized apps appear here once they connect.', level: 'info' }],
    },
  },
  {
    id: 'podcasts',
    aiSummary: 'Turn podcast/video links into attributed markdown summaries in the library or journal.',
    name: 'Podcast Summaries',
    description:
      'Paste a list of podcasts or videos and a plain-English instruction; Lapo transcribes, summarizes, and drops attributed markdown into your library or today’s journal.',
    icon: 'podcasts',
    category: 'productivity',
    runnable: false,
    available: true,
    requires: [],
    config: [
      { label: 'Sources', value: 'Apple, Spotify, YouTube, or direct links', hint: 'Freeform — links, titles, or both, in any order.' },
      { label: 'Transcripts', value: 'Taddy → captions → AssemblyAI STT', hint: 'BYO keys; falls back to Gemini audio when a key is absent.' },
      { label: 'Output', value: 'Compiled doc, per-episode docs, or journal' },
      { label: 'Summaries', value: 'Metered Gemini via QUILL', hint: 'Summaries only + always attributed; never verbatim transcript.' },
    ],
    demo: {
      status: 'paused',
      audit: [{ agoMinutes: 0, event: 'Ready', detail: 'Add a Taddy or AssemblyAI key, then paste links to summarize.', level: 'info' }],
    },
  },
];

/** Build the client-facing DTO for a definition using its demo seed (offline/fallback rendering). */
export function fallbackIntegration(def: IntegrationDefinition): Integration {
  return {
    id: def.id,
    name: def.name,
    description: def.description,
    icon: def.icon,
    category: def.category,
    status: def.demo.status,
    enabled: true,
    runnable: isRunnable(def),
    connect: def.connect,
    lastAction: def.demo.lastAction,
    config: def.config,
    audit: def.demo.audit,
  };
}

/**
 * The offline/demo catalog — the client renders this when it can't reach the API. Derived from
 * {@link INTEGRATION_DEFS} so it never drifts from the source of truth.
 */
export const INTEGRATIONS: Integration[] = INTEGRATION_DEFS.map(fallbackIntegration);

// --- Contact Pictures headshot studio ----------------------------------------------------------

/**
 * A headshot style preset — the shared source of truth for the studio. The UI renders these as
 * selectable chips (id + label + blurb); the server turns the chosen `promptModifier` into the
 * instruction it sends the image model alongside the uploaded reference photos. Keeping them here
 * (in core) means the client and server never disagree about which styles exist or what they mean.
 */
export interface HeadshotStyle {
  /** Stable slug sent from the client and stored on the draft/audit record. */
  id: string;
  /** Chip label. */
  label: string;
  /** One-line description shown under the chip. */
  blurb: string;
  /** Style-specific clause appended to the base headshot prompt. */
  promptModifier: string;
}

/** The base instruction every generation shares; a style's `promptModifier` is appended to it. */
export const HEADSHOT_BASE_PROMPT =
  'Generate a single high-resolution, photorealistic professional headshot of the same person shown in the ' +
  'reference photos. Preserve their identity, facial features, skin tone, and approximate age. Frame head and ' +
  'shoulders, centered, sharp focus, flattering even lighting, clean background. Do not add text, watermarks, ' +
  'logos, or other people.';

export const HEADSHOT_STYLES: HeadshotStyle[] = [
  {
    id: 'modern',
    label: 'Modern',
    blurb: 'Contemporary, approachable, soft studio light.',
    promptModifier: 'Contemporary editorial style: relaxed smart-casual attire, soft neutral gradient background, warm natural light.',
  },
  {
    id: 'business',
    label: 'Business',
    blurb: 'Corporate LinkedIn-ready portrait.',
    promptModifier: 'Corporate business style: dark blazer over a collared shirt, muted grey studio background, crisp professional lighting.',
  },
  {
    id: 'formal',
    label: 'Formal',
    blurb: 'Classic, refined, executive.',
    promptModifier: 'Formal executive style: tailored suit and tie, deep neutral backdrop, dramatic Rembrandt lighting.',
  },
  {
    id: 'casual',
    label: 'Casual',
    blurb: 'Relaxed, friendly, everyday.',
    promptModifier: 'Casual style: open-collar shirt or knit, bright airy background, cheerful daylight, natural relaxed expression.',
  },
  {
    id: 'creative',
    label: 'Creative',
    blurb: 'Bold, colorful, characterful.',
    promptModifier: 'Creative style: expressive lighting with a bold colored accent background, artistic contrast, confident personality.',
  },
];

/** Look up a style preset by id. */
export function findHeadshotStyle(id: string): HeadshotStyle | undefined {
  return HEADSHOT_STYLES.find((s) => s.id === id);
}

/** Build the full image-model prompt for a style id (falls back to the base prompt alone). */
export function headshotPrompt(styleId: string): string {
  const style = findHeadshotStyle(styleId);
  return style ? `${HEADSHOT_BASE_PROMPT} ${style.promptModifier}` : HEADSHOT_BASE_PROMPT;
}

/** Image-model tiers the studio can target. Flash is the default workhorse; Pro is premium. */
export const HEADSHOT_MODELS = {
  flash: 'gemini-3.1-flash-image',
  pro: 'gemini-3-pro-image',
} as const;

export type HeadshotModelTier = keyof typeof HEADSHOT_MODELS;
